
export type RemoteDisplaySocketMetrics = {
    bytesIn: number;
    bytesOut: number;
};

export type RemoteDisplaySocketMessage =
    | {
        kind: 'control';
        raw: string;
        payload: unknown;
      }
    | {
        kind: 'binary';
        data: ArrayBuffer | Blob;
        size: number;
      };

export interface RemoteDisplaySocketBoundaryOptions {
    url: string;
    binaryType?: BinaryType;
    parseControlMessage?: (text: string) => unknown;
    onOpen?: () => void;
    onMessage?: (message: RemoteDisplaySocketMessage) => void;
    onClose?: () => void;
    onError?: () => void;
    onMetrics?: (metrics: RemoteDisplaySocketMetrics) => void;
}

function defaultParseControlMessage(text: string): unknown {
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
}

function measureOutboundSize(value: unknown): number {
    if (typeof value === 'string') {
        return new TextEncoder().encode(value).byteLength;
    }
    if (value instanceof ArrayBuffer) {
        return value.byteLength;
    }
    if (ArrayBuffer.isView(value)) {
        return value.byteLength;
    }
    if (value instanceof Blob) {
        return value.size;
    }
    return 0;
}

function measureInboundSize(value: unknown): number {
    if (typeof value === 'string') {
        return value.length;
    }
    if (value instanceof ArrayBuffer) {
        return value.byteLength;
    }
    if (value instanceof Blob) {
        return value.size;
    }
    return Number((value as { size?: number })?.size || 0);
}

function closeWebSocketBestEffort(socket: { close?: () => void } | null | undefined): boolean {
    try {
        socket?.close?.();
        return true;
    } catch (_error) {
        return false;
    }
}

export class WebSocketRemoteDisplayBoundary {
    private socket: WebSocket | null = null;
    private disposed = false;
    private readonly options: RemoteDisplaySocketBoundaryOptions;
    private bytesIn = 0;
    private bytesOut = 0;
    private pendingOutbound: Array<string | ArrayBuffer | ArrayBufferView | Blob> = [];

    constructor(options: RemoteDisplaySocketBoundaryOptions) {
        this.options = options;
    }

    connect(): void {
        if (this.disposed) return;
        closeWebSocketBestEffort(this.socket);
        const socket = new WebSocket(this.options.url);
        socket.binaryType = this.options.binaryType || 'arraybuffer';
        socket.addEventListener('open', () => {
            if (this.disposed || this.socket !== socket) return;
            this.flushPendingOutbound(socket);
            this.options.onOpen?.();
        });
        socket.addEventListener('message', (event) => {
            if (this.disposed || this.socket !== socket) return;
            const size = measureInboundSize(event.data);
            this.bytesIn += size;
            this.emitMetrics();
            if (typeof event.data === 'string') {
                const parser = this.options.parseControlMessage || defaultParseControlMessage;
                this.options.onMessage?.({
                    kind: 'control',
                    raw: event.data,
                    payload: parser(event.data),
                });
                return;
            }
            this.options.onMessage?.({
                kind: 'binary',
                data: event.data,
                size,
            });
        });
        socket.addEventListener('close', () => {
            if (this.socket === socket) {
                this.socket = null;
            }
            if (this.disposed) return;
            this.options.onClose?.();
        });
        socket.addEventListener('error', () => {
            if (this.disposed || this.socket !== socket) return;
            this.options.onError?.();
        });
        this.socket = socket;
    }

    send(data: string | ArrayBuffer | ArrayBufferView | Blob): void {
        if (this.disposed) return;
        const socket = this.socket;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
            this.pendingOutbound.push(data);
            return;
        }
        this.sendNow(socket, data);
    }

    sendControl(payload: unknown): void {
        this.send(JSON.stringify(payload ?? {}));
    }

    getMetrics(): RemoteDisplaySocketMetrics {
        return {
            bytesIn: this.bytesIn,
            bytesOut: this.bytesOut,
        };
    }

    dispose(): void {
        if (this.disposed) return;
        this.disposed = true;
        closeWebSocketBestEffort(this.socket);
        this.socket = null;
    }

    private emitMetrics(): void {
        this.options.onMetrics?.(this.getMetrics());
    }

    private sendNow(socket: WebSocket, data: string | ArrayBuffer | ArrayBufferView | Blob): void {
        const size = measureOutboundSize(data);
        this.bytesOut += size;
        this.emitMetrics();
        socket.send(data as string | Blob | BufferSource);
    }

    private flushPendingOutbound(socket: WebSocket): void {
        if (this.pendingOutbound.length === 0) return;
        const pending = this.pendingOutbound.splice(0);
        for (let index = 0; index < pending.length; index += 1) {
            if (this.disposed || this.socket !== socket || socket.readyState !== WebSocket.OPEN) {
                this.pendingOutbound.unshift(...pending.slice(index));
                return;
            }
            this.sendNow(socket, pending[index]);
        }
    }
}
